import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import { ScheduledPlanChangeIndex } from '../api/types';
import config from '../config';

interface ScheduledPlanChangeQueryParams {
  axios: AxiosInstance;
  sortOrder: string;
  itemsPerPage: number;
  page: number;
  workshopId?: number;
}
async function getScheduledPlanChanges({
  axios,
  sortOrder,
  itemsPerPage,
  page,
  workshopId,
}: ScheduledPlanChangeQueryParams): Promise<ScheduledPlanChangeIndex> {
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/scheduled-plan-changes`, {
    params: {
      sortOrder: sortOrder,
      itemsPerPage: itemsPerPage,
      page: page,
      workshopId: workshopId,
    },
  });
  return data;
}
export default function useScheduledPlanChangesQuery({ sortOrder, itemsPerPage, page, workshopId }: Omit<ScheduledPlanChangeQueryParams, 'axios'>) {
  const axios = useAxios();
  const toast = useToast();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey({
      sortOrder,
      itemsPerPage,
      page,
      workshopId,
    }),
    queryFn: () =>
      getScheduledPlanChanges({
        axios,
        sortOrder,
        itemsPerPage,
        page,
        workshopId,
      }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, query.error as any);
    }
  }, [query.error, query.isError, toast]);

  return query;
}

function getKey({ sortOrder, itemsPerPage, page, workshopId }: Omit<ScheduledPlanChangeQueryParams, 'axios'>) {
  return ['Scheduled Plan Changes', sortOrder, itemsPerPage, page, workshopId] as const satisfies QueryKey;
}
getScheduledPlanChanges.getKey = getKey;
